import { getApiBaseUrl } from './apiBase.js';
import { startFreshChat } from './storage.js';

const API_BASE_URL = getApiBaseUrl();

const extractProfileUser = (profile) => profile?.data?.user || profile?.user || null;

const authService = {
  // Store token in localStorage
  setToken(token) {
    localStorage.setItem('authToken', token);
  },

  // Get token from localStorage
  getToken() {
    return localStorage.getItem('authToken');
  },

  // Remove token from localStorage
  removeToken() {
    localStorage.removeItem('authToken');
  },

  decodeToken(token = this.getToken()) {
    if (!token) return null;
    try {
      return JSON.parse(atob(token.split('.')[1]));
    } catch {
      return null;
    }
  },

  isTokenExpired(token = this.getToken()) {
    const payload = this.decodeToken(token);
    if (!payload) return true;
    if (!payload.exp) return false;
    return payload.exp * 1000 <= Date.now();
  },

  isSessionExpiredError(error) {
    const status = error?.status;
    const message = String(error?.message || '');
    return status === 401 || /token has expired|invalid token|please log in again/i.test(message);
  },

  redirectToLogin() {
    this.removeToken();
    const base = String(import.meta.env.VITE_BASE_PATH || '').replace(/\/$/, '');
    window.location.assign(`${base}/login`);
  },

  // Check if user is authenticated
  isAuthenticated() {
    const token = this.getToken();
    if (!token) return false;
    if (this.isTokenExpired(token)) {
      this.removeToken();
      return false;
    }
    return true;
  },

  // Get auth headers
  getAuthHeaders() {
    const token = this.getToken();
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  },

  // Register
  async register(email, password, firstName, lastName) {
    const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password, firstName, lastName })
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Registration failed');
    }
    return data;
  },

  // Login
  async login(email, password) {
    let response;
    try {
      response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password })
      });
    } catch {
      throw new Error(
        'Cannot reach the API server. Start the backend on port 3000 (see backend/env.example for required .env settings).'
      );
    }

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Login failed');
    }

    // If token is returned, store it and open a fresh chat (earlier chats stay in Recent)
    if (data.token) {
      this.setToken(data.token);
      startFreshChat();
    }

    return data;
  },

  // Verify 2FA
  async verify2FA(userId, code) {
    const response = await fetch(`${API_BASE_URL}/api/auth/verify-2fa`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId, token: code })
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || '2FA verification failed');
    }

    // Store token and open a fresh chat (earlier chats stay in Recent)
    if (data.token) {
      this.setToken(data.token);
      startFreshChat();
    }

    return data;
  },

  // Verify Email
  async verifyEmail(token) {
    const response = await fetch(`${API_BASE_URL}/api/auth/verify-email/${token}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Email verification failed');
    }
    return data;
  },

  // Forgot Password
  async forgotPassword(email) {
    const response = await fetch(`${API_BASE_URL}/api/auth/forgot-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email })
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Failed to send password reset email');
    }
    return data;
  },

  // Reset Password
  async resetPassword(token, password, companyId) {
    const qs = companyId ? `?c=${encodeURIComponent(companyId)}` : '';
    const response = await fetch(`${API_BASE_URL}/api/auth/reset-password/${encodeURIComponent(token)}${qs}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ password })
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Password reset failed');
    }
    return data;
  },

  // Logout
  async logout() {
    const token = this.getToken();
    const sessionId = typeof window !== 'undefined' 
      ? localStorage.getItem('nova-chat-session-id') 
      : null;
    
    // 1. Delete backend session if sessionId exists
    if (sessionId && token) {
      try {
        const deleteResponse = await fetch(`${API_BASE_URL}/api/chat/session/${sessionId}`, {
          method: 'DELETE',
          headers: this.getAuthHeaders()
        });
        if (deleteResponse.ok) {
          console.log('Backend session cleared on logout');
        } else if (deleteResponse.status !== 404) {
          // 404 is OK - session might not exist
          console.warn('Failed to clear backend session on logout:', deleteResponse.status);
        }
      } catch (error) {
        // Non-critical error - continue with logout
        console.warn('Error clearing backend session on logout (non-critical):', error);
      }
    }
    
    // 2. Call logout API endpoint
    if (token) {
      try {
        await fetch(`${API_BASE_URL}/api/auth/logout`, {
          method: 'POST',
          headers: this.getAuthHeaders()
        });
      } catch (error) {
        console.error('Logout API call failed:', error);
      }
    }
    
    // 3. Clear frontend localStorage
    this.removeToken();
    
    // Clear chat sessions and history on logout for tenant isolation
    try {
      const { clearMessageHistory, clearSessionId } = await import('./storage.js');
      clearMessageHistory();
      clearSessionId();
    } catch (error) {
      console.error('Failed to clear chat sessions on logout:', error);
    }
  },

  // Get Profile
  async getProfile() {
    const response = await fetch(`${API_BASE_URL}/api/auth/profile`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    const data = await response.json();
    if (!response.ok) {
      console.error('Profile API error:', data);
      const error = new Error(data.message || 'Failed to get profile');
      error.status = response.status;
      throw error;
    }
    return data;
  },

  // Check if user is admin (includes superadmin)
  // First checks JWT token, but also allows async check from profile
  isAdmin() {
    try {
      const token = this.getToken();
      if (!token) return false;
      // Decode JWT to get role (simple base64 decode, no verification needed for client-side check)
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.role === 'admin' || payload.role === 'superadmin';
    } catch (error) {
      return false;
    }
  },

  // Check if user is super admin
  isSuperAdmin() {
    try {
      const token = this.getToken();
      if (!token) return false;
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.role === 'superadmin';
    } catch (error) {
      return false;
    }
  },

  // Async check admin status from profile (more reliable, checks database)
  // Returns true for both admin and superadmin roles
  async checkAdminStatus() {
    try {
      const profile = await this.getProfile();
      const user = extractProfileUser(profile);
      if (user?.hasEffectiveAdminAccess === true) return true;
      if (user?.hasEffectiveAdminAccess === false) return false;
      const role = user?.effectiveRole || user?.role || profile?.role;
      return role === 'admin' || role === 'superadmin';
    } catch (error) {
      console.error('Error checking admin status:', error);
      // Fallback to JWT token check
      return this.isAdmin();
    }
  },

  // Async check super admin status from profile
  async checkSuperAdminStatus() {
    try {
      const profile = await this.getProfile();
      const user = extractProfileUser(profile);
      const role = user?.effectiveRole || user?.role || profile?.role;
      return role === 'superadmin';
    } catch (error) {
      console.error('Error checking super admin status:', error);
      // Fallback to JWT token check
      return this.isSuperAdmin();
    }
  },

  // Get user role
  getUserRole() {
    try {
      const token = this.getToken();
      if (!token) return null;
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.role || 'user';
    } catch (error) {
      return null;
    }
  },

  // Load full user auth context from profile
  async getAuthContext() {
    const profile = await this.getProfile();
    const user = extractProfileUser(profile);
    if (!user) return null;

    return {
      role: user.role || 'user',
      effectiveRole: user.effectiveRole || user.role || 'user',
      trial: user.trial || null,
      canUseChat: user.canUseChat,
      hasEffectiveAdminAccess: user.hasEffectiveAdminAccess,
      requiresPaywall: user.requiresPaywall,
      subscriptionStatus: user.subscriptionStatus,
      user
    };
  },

  // Change Password
  async changePassword(currentPassword, newPassword) {
    const response = await fetch(`${API_BASE_URL}/api/auth/change-password`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ currentPassword, newPassword })
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Password change failed');
    }
    return data;
  }
};

export default authService;

